import { get_brand } from "@alpitour/resources/brands";

const FILTER_HEADINGS = {
  keyd: { icon: "pin", title: "Località" },
  accommodation: { icon: "bed", title: "Trattamento" },
  rating: { icon: "star", title: "Categoria" },
  airport: { icon: "airplane", title: "Aeroporto" },
  brand: { icon: "symbol_vv", title: "Brand" },
};

/**
 * Normalization to ensure same label format across different data
 * @param {string} rating hotel/property category (3 stars, 4 stars, etc...)
 * @returns {string} Formatted rating label for filters 
 */
const build_rating_label = (raw) => {
  if (!raw) { 
    return raw;
  }
  const [rating] = String(raw).split(' ');
  if (isNaN(rating)) {
    return raw
  }
  return `${rating} stell${String(rating) !== '1' ? 'e' : 'a'}`;
}

/**
 * Get the icon and title for a filter category.
 */
export function get_filter_heading(key) {
  return FILTER_HEADINGS[key] || { icon: "symbol_lb", title: "" };
}

/**
 * Return filters without the brand category.
 */
export function get_non_brand_filters(filters) {
  // eslint-disable-next-line no-unused-vars
  const { brand, ...rest } = filters
  return rest
}

const find_static_entry = ({ product, statics, is_only_stay = false }) => {
  const { keyd } = product || {};
  if (!keyd) {
    return;
  }

  if (is_only_stay) {
    const formatted_keyd = `${keyd.slice(2, keyd.length)}-${keyd.slice(0, 2)}`;
    return statics[formatted_keyd];
  }

  return Object.values(statics).find((s) => keyd === s.keyd);
};

/**
 * Build filter options from products and currently filtered results.
 * Returns an object with keyd, accommodation, rating, (airport), brand arrays.
 */
export function build_filters({
  products,
  filtered,
  statics,
  active_filters,
  is_only_stay = false,
}) {
  // Pre-compute count objects in a single pass — O(N) instead of O(N^2)
  const keyd_counts = {};
  const accommodation_counts = {};
  const rating_counts = {};
  const airport_counts = {};
  const brand_counts = {};

  // keyd counts from ALL products
  products.forEach((p) => {
    keyd_counts[p.keyd] = (keyd_counts[p.keyd] || 0) + 1;
  });

  // other counts from filtered products
  filtered.forEach((p) => {
    accommodation_counts[p.accommodation] = (accommodation_counts[p.accommodation] || 0) + 1;
    rating_counts[p.rating] = (rating_counts[p.rating] || 0) + 1;
    if (!is_only_stay) {
      airport_counts[p.departureAirportCode] = (airport_counts[p.departureAirportCode] || 0) + 1;
    }
    brand_counts[p.brand] = (brand_counts[p.brand] || 0) + 1;
  });

  // Build unique filter entries — seen object for O(1) deduplication
  const seen = {};
  const filters = {
    keyd: [],
    accommodation: [],
    rating: [],
    ...(!is_only_stay ? { airport: [] } : {}),
    brand: [],
  };

  products.forEach((product) => {
    // keyd
    if (!seen[`keyd:${product.keyd}`]) {
      seen[`keyd:${product.keyd}`] = true;
      const static_entry = find_static_entry({
        product,
        statics,
        is_only_stay,
      });
      filters.keyd.push({
        text: static_entry ? static_entry.city : product.keyd,
        value: product.keyd,
        amount: keyd_counts[product.keyd] || 0,
        selected: active_filters.filter_keyd.includes(product.keyd),
      });
    }

    // accommodation
    if (!seen[`accommodation:${product.accommodation}`]) {
      seen[`accommodation:${product.accommodation}`] = true;
      filters.accommodation.push({
        text: product.accommodation,
        value: product.accommodation,
        amount: accommodation_counts[product.accommodation] || 0,
        selected: active_filters.filter_accommodation.includes(product.accommodation),
      });
    }

    // rating
    if (!seen[`rating:${product.rating}`]) {
      seen[`rating:${product.rating}`] = true;
      filters.rating.push({
        text: build_rating_label(product.rating),
        value: product.rating,
        amount: rating_counts[product.rating] || 0,
        selected: active_filters.filter_rating.includes(product.rating),
      });
    }

    // airport (skipped for only-stay)
    if (filters.airport && !seen[`airport:${product.departureAirportCode}`]) {
      seen[`airport:${product.departureAirportCode}`] = true;
      filters.airport.push({
        text: product.departureAirportDescription,
        value: product.departureAirportCode,
        amount: airport_counts[product.departureAirportCode] || 0,
        selected: active_filters.filter_airport.includes(product.departureAirportCode),
      });
    }

    // brand
    if (!seen[`brand:${product.brand}`]) {
      seen[`brand:${product.brand}`] = true;
      const brand = get_brand(product.brand);
      filters.brand.push({
        text: brand.name,
        value: product.brand,
        amount: brand_counts[product.brand] || 0,
        selected: active_filters.filter_brand.includes(product.brand),
        logo: brand.logo,
      });
    }
  });

  // due keyd diversi possono riferirsi alla stessa città (specie in only_stay),
  // generando righe duplicate nel filtro Località. Raggruppo per `text`, sommo gli amount e accumulo i keyd in un array.
  // apply_filters riceve comunque un array piatto di keyd grazie al flatMap nel dispatcher (results-controls.on_modal_filter).
  const keyd_groups = {};
  filters.keyd.forEach((entry) => {
    const group_key = entry.text;
    if (!keyd_groups[group_key]) {
      keyd_groups[group_key] = {
        text: entry.text,
        value: [entry.value],
        amount: entry.amount,
        selected: entry.selected,
      };
    } else {
      keyd_groups[group_key].value.push(entry.value);
      keyd_groups[group_key].amount += entry.amount;
      keyd_groups[group_key].selected = keyd_groups[group_key].selected || entry.selected;
    }
  });
  filters.keyd = Object.values(keyd_groups);

  // sort each category
  Object.keys(filters).forEach((key) => {
    if (key === "rating") {
      filters[key].sort((a, b) => {
        if (a.text === b.text) {
          return 0;
        }
        return a.text < b.text ? 1 : -1;
      });
    } else {
      filters[key].sort((a, b) => {
        if (a.text === b.text) {
          return 0;
        }
        return a.text >= b.text ? 1 : -1;
      });
    }
  });

  return filters;
}

/**
 * Apply all active filters to a products array.
 * Returns the filtered array (does not mutate the original).
 * Returns null if a filter produces empty results (caller should reset that filter).
 */
export function apply_filters({
  products,
  active_filters,
  availability_total,
  markup_fn,
}) {
  let result = [...products];

  // availability
  if (active_filters.filter_availability && markup_fn) {
    result = result.filter(
      (p) => markup_fn(p.price).display_price <= availability_total,
    );
  }

  // keyd
  if (active_filters.filter_keyd.length) {
    result = result.filter((p) => active_filters.filter_keyd.includes(p.keyd));
  }

  // accommodation
  if (active_filters.filter_accommodation.length) {
    const filtered = result.filter((p) =>
      active_filters.filter_accommodation.includes(p.accommodation),
    );
    if (filtered.length === 0) {
      return { result: null, empty_filter: "accommodation" };
    }
    result = filtered;
  }

  // rating
  if (active_filters.filter_rating.length) {
    const filtered = result.filter((p) =>
      active_filters.filter_rating.includes(p.rating),
    );
    if (filtered.length === 0) {
      return { result: null, empty_filter: "rating" };
    }
    result = filtered;
  }

  // airport
  if (active_filters.filter_airport && active_filters.filter_airport.length) {
    const filtered = result.filter((p) =>
      active_filters.filter_airport.includes(p.departureAirportCode),
    );
    if (filtered.length === 0) {
      return { result: null, empty_filter: "airport" };
    }
    result = filtered;
  }

  // brand
  if (active_filters.filter_brand.length) {
    const filtered = result.filter((p) =>
      active_filters.filter_brand.includes(p.brand),
    );
    if (filtered.length === 0) {
      return { result: null, empty_filter: "brand" };
    }
    result = filtered;
  }

  return { result, empty_filter: null };
}
